/**
 * Multimodal intake helpers — turn a dropped/pasted/picked File (or URL / code)
 * into a MultiAgentAttachment, and resolve it to markdown via the backend
 * /preprocess (Gemini for image/pdf/audio, fetch for URL, passthrough for
 * text/code). Image/pdf/audio resolve on the server; text/code resolve locally.
 */
import {
  preprocessAttachment,
  type MultiAgentAttachment,
  type MultiAgentAttachmentKind,
} from "@/services/multi-agent/api";
import { downscaleImage } from "@/lib/imageResize";

export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2MB
export const MAX_ATTACHMENTS = 6;
export const FILE_ACCEPT =
  "image/*,.pdf,.txt,.md,.markdown,.cls,.trigger,.apex,.js,.ts,.tsx,.jsx,.json,.html,.css,.xml,.yaml,.yml,.py,.java,.sql,.csv";
export const AUDIO_ACCEPT = "audio/*,.mp3,.wav,.m4a,.aac,.aiff,.ogg,.flac,.webm";

// Code file extensions (lowercased) treated as inline code attachments.
const CODE_EXTENSIONS = new Set([
  "cls", "trigger", "apex", "js", "ts", "tsx", "jsx", "json", "html", "css",
  "xml", "yml", "yaml", "py", "java", "sql", "sh", "rb", "go", "rs",
  "c", "cpp", "cs",
]);

function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Lowercased file extension after the last dot, or undefined when there's no
 *  real extension (no dot, or the dot is at position 0 — i.e. no basename). */
function extFromName(name: string): string | undefined {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return undefined;
  return name.slice(dot + 1).toLowerCase();
}

function langFromName(name: string): string | undefined {
  const ext = extFromName(name);
  if (!ext) return undefined;
  const map: Record<string, string> = {
    cls: "apex", trigger: "apex", apex: "apex", ts: "typescript", tsx: "typescript",
    js: "javascript", jsx: "javascript", py: "python", java: "java", sql: "sql",
    html: "html", css: "css", json: "json", xml: "xml", yml: "yaml", yaml: "yaml",
  };
  return map[ext] || ext;
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      resolve(s.slice(s.indexOf(",") + 1));
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const AUDIO_EXTENSIONS = new Set([
  "mp3", "wav", "m4a", "aac", "aiff", "ogg", "flac", "webm",
]);

export function kindForFile(file: File): MultiAgentAttachmentKind {
  const t = file.type || "";
  const ext = extFromName(file.name);
  if (t.startsWith("image/")) return "image";
  if (t === "application/pdf" || ext === "pdf") return "pdf";
  if (t.startsWith("audio/") || (ext !== undefined && AUDIO_EXTENSIONS.has(ext))) return "audio";
  if (ext !== undefined && CODE_EXTENSIONS.has(ext)) return "code";
  return "text";
}

/** File → unresolved attachment (reads bytes/text + image thumbnail). */
export async function fileToAttachment(original: File): Promise<MultiAgentAttachment | { error: string }> {
  // Images: canvas-downscale BEFORE the 2MB gate so big retina/4K screenshot
  // pastes stop being silently rejected and the Gemini payload shrinks.
  let file = original;
  if ((original.type || "").startsWith("image/") && original.type !== "image/svg+xml") {
    try { file = await downscaleImage(original); } catch { /* keep original */ }
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return { error: `${file.name} is over 2MB` };
  const kind = kindForFile(file);
  const att: MultiAgentAttachment = { id: uid(), kind, name: file.name, status: "processing" };
  if (kind === "image") {
    att.mime = file.type || "image/png";
    att.data_base64 = await readBase64(file);
    att.thumbnailDataUrl = `data:${att.mime};base64,${att.data_base64}`;
  } else if (kind === "pdf" || kind === "audio") {
    att.mime = file.type || (kind === "pdf" ? "application/pdf" : "audio/mpeg");
    att.data_base64 = await readBase64(file);
  } else if (kind === "code") {
    att.language = langFromName(file.name);
    att.content = await file.text();
    att.markdown = "```" + (att.language || "") + "\n" + att.content + "\n```";
    att.status = "ready";
  } else {
    att.content = await file.text();
    att.markdown = att.content;
    att.status = "ready";
  }
  return att;
}

export function urlAttachment(url: string): MultiAgentAttachment {
  return { id: uid(), kind: "url", name: url, content: url, status: "processing" };
}

/** A recorded WAV blob (from the mic) → audio attachment for transcription. */
export async function audioBlobToAttachment(
  blob: Blob,
  name = "voice note.wav",
): Promise<MultiAgentAttachment | { error: string }> {
  if (blob.size > MAX_ATTACHMENT_BYTES) return { error: "Recording too long (over 2MB)" };
  const b64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); resolve(s.slice(s.indexOf(",") + 1)); };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  return { id: uid(), kind: "audio", name, mime: "audio/wav", data_base64: b64, status: "processing" };
}

export function pastedCodeAttachment(text: string): MultiAgentAttachment {
  return {
    id: uid(), kind: "code", name: "pasted code", content: text, language: "",
    markdown: "```\n" + text + "\n```", status: "ready",
  };
}

/** Map a raw backend fetch/extract error to plain user guidance. Plain string
 *  matching only (no regex per project rule). Falls back to the raw reason. */
export function friendlyAttachmentError(raw: string | null | undefined, kind: MultiAgentAttachmentKind): string {
  const reason = (raw || "").trim();
  const low = reason.toLowerCase();
  if (low.includes("non-public") || low.includes("private") || low.includes("loopback") || low.includes("not allowed for this address"))
    return "That address is private/internal — only public web pages can be fetched.";
  if (low.includes("content-type") || low.includes("content type") || low.includes("not allowed"))
    return "That page type can't be read (only HTML / plain text is supported).";
  if (low.includes("timeout") || low.includes("timed out"))
    return "The page took too long to load — try again or paste the text directly.";
  if (low.includes("no readable text") || low.includes("empty"))
    return kind === "url" ? "That page had no readable text to extract." : "Nothing readable could be extracted.";
  if (low.includes("could not be fetched") || low.includes("dns") || low.includes("connection") || low.includes("refused"))
    return "Couldn't reach that page — check the URL and that it's publicly accessible.";
  return reason || "could not process this attachment";
}

/** Resolve a server-side kind (image/pdf/audio/url) → markdown. text/code are
 *  already resolved locally. Returns the updated attachment. */
export async function resolveAttachment(att: MultiAgentAttachment): Promise<MultiAgentAttachment> {
  if (att.markdown && att.status === "ready") return att; // already resolved (text/code)
  try {
    const r = await preprocessAttachment({
      kind: att.kind, name: att.name, mime: att.mime,
      language: att.language, content: att.content, data_base64: att.data_base64,
    });
    const ok = r.ok !== false && !!(r.markdown && r.markdown.trim());
    // Truncation signal (image/pdf/audio): explicit flag OR a non-STOP finish.
    const truncated =
      r.truncated === true ||
      (typeof r.finish_reason === "string" &&
        r.finish_reason.length > 0 &&
        r.finish_reason.toUpperCase() !== "STOP");
    return {
      ...att,
      markdown: r.markdown || "",
      status: ok ? "ready" : "error",
      errorReason: ok ? undefined : friendlyAttachmentError(r.error, att.kind),
      // URL chip metadata (optional — undefined when the backend doesn't send it).
      title: r.title || att.title,
      favicon: r.favicon || att.favicon,
      truncated: truncated || undefined,
    };
  } catch (e) {
    return {
      ...att,
      status: "error",
      errorReason: friendlyAttachmentError((e as Error)?.message, att.kind),
    };
  }
}
